"use client";

import type React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Flex, Spinner, useToast } from "@chakra-ui/react";
import EmailAgentConfig from "./EmailAgentConfig";
import {
  emailAgentService,
  type EmailAgent,
} from "../../email-services/agentService";
import { useAuth } from "../../contexts/AuthContext";

const EmailAgentRouter: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { credits, creditsLoading } = useAuth(); // Get creditsLoading state
  const [agent, setAgent] = useState<EmailAgent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasMountedRef = useRef(false);
  const hasShownCreditWarning = useRef(false);

  useEffect(() => {
    if (
      !hasMountedRef.current &&
      !creditsLoading &&
      credits !== null &&
      credits < 0.01 &&
      !hasShownCreditWarning.current
    ) {
      hasShownCreditWarning.current = true;
      toast({
        title: "Insufficient Credits",
        description:
          "You need to have credits in your wallet in order to access email agents.",
        status: "error",
        duration: 5000,
      });
      navigate("/email-agents");
    }
    hasMountedRef.current = true;
  }, [credits, creditsLoading, navigate, toast]); // Add creditsLoading to dependencies

  useEffect(() => {
    const fetchAgent = async () => {
      if (!agentId) return;

      try {
        const fetchedAgent = await emailAgentService.get(agentId);
        setAgent(fetchedAgent);
      } catch (error) {
        console.error("Error fetching Email agent:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgent();
  }, [agentId]);

  if (!agentId || isLoading) {
    return (
      <Flex h="100vh" justify="center" align="center" bg="white">
        <Spinner size="xl" color="orange.500" />
      </Flex>
    );
  }

  if (!agent) {
    return null;
  }

  return <EmailAgentConfig agent={agent} />;
};

export default EmailAgentRouter;
